'use client';

import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';

import OneyLogo from '@/components/ui/icons/payment/svg/oney.svg';
import { Icon } from '@/components/ui/Icon';
import { getAvailableOneyInstallments, getOneyInstallmentShare } from '@/lib/product/oneyPlans';
import { formatPrice } from '@/lib/utils/formatPrice';
import { modalActions } from '@/states/modalState';

import styles from './OneySentence.module.scss';

export interface OneySentenceProps {
  amount: number;
  currency: string;
  ctaType: string;
  oneyEnabled: boolean;
  oney3xMin?: number;
  oney3xMax?: number;
  oney4xMin?: number;
  oney4xMax?: number;
  className?: string;
}

/**
 * French Oney teaser on PDP — pay-in badges + down payment hint; opens OneyModal on click.
 * Visibility matches legacy Breitling: enabled, ORDER CTA, amount within 3x/4x limits.
 */
export function OneySentence({
  amount,
  currency,
  ctaType,
  oneyEnabled,
  oney3xMin,
  oney3xMax,
  oney4xMin,
  oney4xMax,
  className,
}: Readonly<OneySentenceProps>) {
  const t = useTranslations('product.oney');
  const locale = useLocale();

  if (!oneyEnabled || ctaType !== 'ORDER') {
    return null;
  }

  const availableInstallments = getAvailableOneyInstallments(
    amount,
    oney3xMin,
    oney3xMax,
    oney4xMin,
    oney4xMax
  );

  if (availableInstallments.length === 0) {
    return null;
  }

  // Breitling parity: sentence always uses first available plan for the amount,
  // and hardcodes remaining installments to 2 (legacy OneyDownPaymentMessage default).
  // Use formatPrice + currencyDisplay symbol so EUR keeps home-locale placement ("3.100 €").
  const firstInstallment = availableInstallments[0];
  const downPaymentAmount = formatPrice(
    getOneyInstallmentShare(amount, firstInstallment),
    currency,
    locale,
    { currencyDisplay: 'symbol' }
  );
  const zeroCost = formatPrice(0, currency, locale, { currencyDisplay: 'symbol' });

  const openModal = () => {
    modalActions.open('oney', {
      price: amount,
      currency,
      oney3xMin,
      oney3xMax,
      oney4xMin,
      oney4xMax,
    });
  };

  return (
    <button
      type="button"
      className={clsx(styles.root, className)}
      onClick={openModal}
      data-testid="oneySentence"
    >
      <div className={styles.header}>
        <OneyLogo className={styles.logo} aria-hidden />
        <span className={styles.payIn}>{t('payIn')}</span>
        {availableInstallments.map((installment) => (
          <span key={installment} className={styles.badge}>
            {installment}x
          </span>
        ))}
      </div>
      <p className={styles.downPayment}>
        {t('sentenceDownPayment', { amount: downPaymentAmount, times: 2 })}
      </p>
      <p className={styles.paymentCost}>
        {t('sentenceCost', { price: zeroCost })}
        <Icon name="info" size={12} className={styles.infoIcon} aria-hidden />
      </p>
    </button>
  );
}

OneySentence.displayName = 'OneySentence';
